import { Component, OnDestroy, OnInit } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ActivatedRoute } from '@angular/router';
import { ModalConfirmComponent } from '../../shared/modal/modal-confirm.component';
import { PopupService } from '../../shared/modal/popup.service';
import { STORAGE_LOCATION_TYPE_ID } from '../../app.constants';
import { InventoryUnit } from '../../inventory-manager/inventory-unit.model';
import { InventoryContext } from '../../inventory-manager/inventory.context';
import { HttpResponse } from '@angular/common/http';
import { AlertService } from '../../shared/alert/alert.service';
import { LocationSearchRequest } from '../../shared/location/model/location-search-request.model';
import { LocationService } from '../../shared/location/service/location.service';
import { Location } from '../../shared/location/model/location';
import { JhiEventManager, JhiLanguageService } from '../../shared/core-utils';
import { InventoryService } from '../../shared/inventory/service/inventory.service';
import { LotService } from '../../shared/inventory/service/lot.service';
import { Lot } from '../../shared/inventory/model/lot.model';
import { SubscriptionsManager } from '../../shared/util/subscriptions-manager';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'jhi-lot-create-dialog',
    templateUrl: './lot-update-dialog.component.html'
})
export class LotUpdateDialogComponent implements OnInit, OnDestroy {

    lotUpdate: Lot;
    units: Promise<InventoryUnit[]>;

    storageLocations: Promise<Location[]>;
    favoriteLocations: Promise<Location[]>;

    storageLocationType = [STORAGE_LOCATION_TYPE_ID];
    storageLocIdSelected;
    favoriteLocIdSelected;
    useFavoriteLocations = false;

    unitsLabelCheck: any;
    storageLocationLabelCheck: any;
    gidLabelCheck: any;
    notesLabelCheck: any;
    subscriptionsManager = new SubscriptionsManager();

    private searchResultDbId: string;

    lotUUIDs: string[];

    constructor(
        public activeModal: NgbActiveModal,
        private jhiLanguageService: JhiLanguageService,
        private modalService: NgbModal,
        private inventoryContext: InventoryContext,
        private activatedRoute: ActivatedRoute,
        private inventoryService: InventoryService,
        private alertService: AlertService,
        private eventManager: JhiEventManager,
        private lotService: LotService,
        private translateService: TranslateService,
        private locationService: LocationService
    ) {
        this.lotUpdate = new Lot();
        this.units = this.inventoryService.queryUnits().toPromise();

        const locationSearchRequest: LocationSearchRequest = new LocationSearchRequest();
        locationSearchRequest.locationTypeIds = this.storageLocationType;
        this.storageLocations = this.locationService.searchLocationsAsObservable(locationSearchRequest, false, null).toPromise();
        this.favoriteLocations = this.locationService.searchLocationsAsObservable(locationSearchRequest, true, null).toPromise();

        this.lotUUIDs = this.inventoryContext.itemIds;
        const queryParams = this.activatedRoute.snapshot.queryParams;

        if (queryParams.searchResultDbId) {
            this.searchResultDbId = queryParams.searchResultDbId;
        }

        this.storageLocations.then(() => {
            if (!queryParams.searchResultDbId && this.lotUUIDs && this.lotUUIDs.length === 1) {
                this.lotService.getById(this.lotUUIDs[0]).then((lot) => {
                    this.lotUpdate = lot;
                    this.storageLocIdSelected = lot.locationId;
                });
            } else {
                this.locationService.getDefaultStorageLocation().toPromise().then((location) => {
                    this.storageLocIdSelected = location.id;
                });
            }
        });

    }

    ngOnDestroy(): void {
        this.subscriptionsManager.unsubscribeAll();
    }

    ngOnInit(): void {
    }

    clear() {
        this.activeModal.dismiss('cancel');
    }

    update(f) {
        this.confirm(f).then(() => {
            const lotUUIDs = this.searchResultDbId ? null : this.lotUUIDs;
            const lotsUpdateRequest = {
                singleInput: {
                    searchComposite: {
                        searchRequest: this.searchResultDbId,
                        itemIds: lotUUIDs
                    },
                }
            };
            Object.assign(lotsUpdateRequest.singleInput, this.getFormValues(f));
            this.subscriptionsManager.add = this.lotService.update(lotsUpdateRequest).subscribe(
                (res: HttpResponse<any>) => this.onSaveSuccess(res.body));

        });
    }

    private confirm(f): Promise<any> {
        let message = '';
        if (!f.form.controls.gid.disabled) {
            message +=
                '<p><strong><i>' +
                this.translateService.instant('inventoryManagerApp.lot.update.reassign.confirmation.info',
                    { param: this.lotUpdate.gid }) +
                '</i></strong></p>';
        }
        if (this.inventoryContext.itemCount > 1) {
            message += this.translateService.instant('inventoryManagerApp.lot.update.confirmation.info',
                { param: this.inventoryContext.itemCount });
        }

        if (!message) {
            return Promise.resolve();
        } else {
            message +=
                this.translateService.instant('inventoryManagerApp.lot.update.confirmation.suffix');
        }

        const confirmModalRef = this.modalService.open(ModalConfirmComponent as Component);
        confirmModalRef.componentInstance.message = message;
        confirmModalRef.componentInstance.title = this.translateService.instant('confirmation');
        return confirmModalRef.result;
    }

    // FIXME is there a way not to rely on form.controls?

    private getFormValues(f) {
        return <Lot>({
            gid: !f.form.controls.gid.disabled ? this.lotUpdate.gid : null,
            locationId: this.getLocationIdFormValue(f),
            unitId: !f.form.controls.unitId.disabled ? this.lotUpdate.unitId : null,
            notes: !f.form.controls.notes.disabled ? this.lotUpdate.notes : null,
        });
    }

    private getLocationIdFormValue(f) {
        if (f.form.controls.useFavoriteLocations.disabled) {
            return '';
        }
        return this.useFavoriteLocations ? this.favoriteLocIdSelected : this.storageLocIdSelected;
    }

    private onSaveSuccess(result: any) {
        this.alertService.success('inventoryManagerApp.lot.update.success');
        this.eventManager.broadcast({ name: 'columnFiltersChanged', content: 'OK' });
        this.activeModal.close(result);
    }

    isFormValid(f) {
        const form = f.form;
        if (!Object.values(form.controls).filter((control: any) => !control.disabled).length) {
            return false;
        }
        return f.form.valid;
    }
}

@Component({
    selector: 'jhi-lot-update-popup',
    template: ''
})
export class LotUpdatePopupComponent implements OnInit, OnDestroy {

    routeSub: any;

    constructor(
        private route: ActivatedRoute,
        private popupService: PopupService
    ) {
    }

    ngOnInit() {
        this.routeSub = this.route.params.subscribe(() => {
            this.popupService.open(LotUpdateDialogComponent as Component);
        });
    }

    ngOnDestroy() {
        this.routeSub.unsubscribe();
    }
}
